/**
 * Gerenciador de Branding - White Label
 * 
 * Este arquivo gerencia o carregamento e aplicação de diferentes brandings de parceiros.
 */

import defaultConfig from '../config';

// ============ IMPORTAR PARCEIROS ============
// Adicione novos parceiros aqui
import telecomplusConfig from './telecomplus';

// Mapa de parceiros disponíveis
const PARCEIROS = {
  onitelecom: defaultConfig,
  telecomplus: telecomplusConfig,
  // Adicione novos parceiros aqui:
  // outroparceiro: outroparceiroConfig,
};

/**
 * Obtém o parceiro ativo baseado em:
 * 1. Variável de ambiente (REACT_APP_PARTNER)
 * 2. SubDomínio (se configurado)
 * 3. localStorage (para testes)
 * 4. Padrão (onitelecom)
 */
export const getActiveParceiro = () => {
  // 1. Verifica variável de ambiente
  const envParceiro = process.env.REACT_APP_PARTNER;
  if (envParceiro && PARCEIROS[envParceiro]) {
    console.log(`[Branding] Carregando parceiro da ENV: ${envParceiro}`);
    return envParceiro;
  }

  // 2. Verifica subdomínio
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  if (PARCEIROS[subdomain]) {
    console.log(`[Branding] Carregando parceiro do subdomínio: ${subdomain}`);
    return subdomain;
  }

  // 3. Verifica localStorage (para testes)
  const localParceiro = localStorage.getItem('activeParceiro');
  if (localParceiro && PARCEIROS[localParceiro]) {
    console.log(`[Branding] Carregando parceiro do localStorage: ${localParceiro}`);
    return localParceiro;
  }

  // 4. Retorna padrão
  console.log('[Branding] Carregando parceiro padrão: onitelecom');
  return 'onitelecom';
};

/**
 * Carrega configuração do parceiro ativo
 */
export const loadBranding = () => {
  const parceiroId = getActiveParceiro();
  const config = PARCEIROS[parceiroId];

  if (!config) {
    console.error(`[Branding] Parceiro não encontrado: ${parceiroId}`);
    return PARCEIROS.onitelecom;
  }

  console.log(`[Branding] Configuração carregada:`, config.client.name);
  return config;
};

/**
 * Aplica o tema CSS do parceiro
 */
export const applyBrandingTheme = (config) => {
  const root = document.documentElement;
  
  // Aplica cores primárias
  if (config.colors?.primary) {
    Object.entries(config.colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });
  }
  
  // Aplica cores secundárias
  if (config.colors?.secondary) {
    Object.entries(config.colors.secondary).forEach(([key, value]) => {
      root.style.setProperty(`--color-secondary-${key}`, value);
    });
  }
  
  // Aplica cores neutras
  if (config.colors?.neutral) {
    Object.entries(config.colors.neutral).forEach(([key, value]) => {
      root.style.setProperty(`--color-neutral-${key}`, value);
    });
  }

  // Atualiza meta tags
  if (config.seo) {
    document.title = config.seo.title;
    
    // Meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', config.seo.description);
    }

    // Theme color
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', config.seo.themeColor);
    }

    // Favicon
    if (config.images?.logos?.favicon) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.setAttribute('href', config.images.logos.favicon);
      }
    }
  }

  console.log(`[Branding] Tema aplicado para: ${config.client.name}`);
};

/**
 * Troca o parceiro ativo (útil para testes)
 */
export const switchParceiro = (parceiroId) => {
  if (!PARCEIROS[parceiroId]) {
    console.error(`[Branding] Parceiro inválido: ${parceiroId}`);
    return false;
  }

  localStorage.setItem('activeParceiro', parceiroId);
  console.log(`[Branding] Parceiro alterado para: ${parceiroId}. Recarregue a página.`);
  
  // Recarrega a página para aplicar novo branding
  window.location.reload();
  return true;
};

/**
 * Lista todos os parceiros disponíveis
 */
export const listParceiros = () => {
  return Object.entries(PARCEIROS).map(([id, config]) => ({
    id,
    name: config.client.name,
    active: config.client.active,
    domain: config.client.domain,
  }));
};

/**
 * Obtém configuração de um parceiro específico
 */
export const getParceiroConfig = (parceiroId) => {
  return PARCEIROS[parceiroId] || null;
};

/**
 * Valida se configuração do parceiro está completa
 */
export const validateParceiroConfig = (config) => {
  const requiredFields = [
    'client.id',
    'client.name',
    'company.name',
    'colors.primary.main',
    'images.logos.main',
  ];

  const missing = [];
  
  requiredFields.forEach(field => {
    const keys = field.split('.');
    let value = config;
    
    for (const key of keys) {
      value = value?.[key];
    }
    
    if (!value) {
      missing.push(field);
    }
  });

  if (missing.length > 0) {
    console.warn('[Branding] Campos obrigatórios faltando:', missing);
    return { valid: false, missing };
  }

  return { valid: true, missing: [] };
};

/**
 * Inicializa o sistema de branding
 * Deve ser chamado no App.jsx
 */
export const initBranding = () => {
  const config = loadBranding();
  const validation = validateParceiroConfig(config);

  if (!validation.valid) {
    console.error('[Branding] Configuração inválida!', validation.missing);
  }

  applyBrandingTheme(config);
  return config;
};

// Exporta configuração ativa
export const activeConfig = loadBranding();

export default {
  getActiveParceiro,
  loadBranding,
  applyBrandingTheme,
  switchParceiro,
  listParceiros,
  getParceiroConfig,
  validateParceiroConfig,
  initBranding,
  activeConfig,
};
